"use client";

import React from "react";
import { Grid, Box, CircularProgress } from "@mui/material";
import mockData from "../../../data/admin_dashboard_mock.json";
import dashboardService from "../../../services/dashboardService";

// Import Components
import SpokeRevenueChart from "./SpokeRevenueChart";
import SpokeLeaderboard from "./SpokeLeaderboard";
import InfluencedRevenueChart from "./InfluencedRevenueChart";
import UserIntelligenceCard from "./UserIntelligenceCard";
import ChatbotHealthCard from "./ChatbotHealthCard";
import LossesCard from "./LossesCard";

const AdminDashboard = () => {
  // Initialize with null or empty structure to avoid showing mock data
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Fetch Spoke Revenue Metrics
        const response = await dashboardService.getSpokeRevenueMetrics();
        
        // Start with mockData structure then overlay API data
        let baseData = { ...mockData };
        
        if (response.success && response.data) {
          baseData = {
            ...baseData,
            spoke_revenue: response.data,
            client_leaderboard: response.data.leaderboard || [],
            chatbot_health: {
              ...baseData.chatbot_health,
              top_reports: response.data.complaints || [],
              top_errors: response.data.errors || []
            }
          };
        }
        setData(baseData);
      } catch (error) {
        console.error("Failed to fetch dashboard metrics", error);
        setData(mockData); // Fallback to mock on error
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading || !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Row 1: Spoke Revenue & Leaderboard */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <SpokeRevenueChart
            data={data.spoke_revenue?.history || []}
            metrics={data.spoke_revenue || {}}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <SpokeLeaderboard
            data={data.client_leaderboard}
          />
        </Grid>
      </Grid>

      {/* Row 2: Influenced Revenue & User Intelligence */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfluencedRevenueChart
            data={data.revenue_influenced}
            breakdown={data.revenue_influenced?.breakdown}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <UserIntelligenceCard
            data={data.user_intelligence}
          />
        </Grid>
      </Grid>

      {/* Row 3: Chatbot Health & Losses */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChatbotHealthCard
            data={data.chatbot_health}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LossesCard
            data={data.losses}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
